import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';

type AuthMethod = 'oauth' | 'token';
type SetupStep = 'connect' | 'configure' | 'sync' | 'advanced';
type StatusTone = 'info' | 'success' | 'error' | 'warning';

@Component({
  selector: 'app-clickup-integration',
  templateUrl: './clickup-integration.component.html',
  styleUrls: ['./clickup-integration.component.css'],
})
export class ClickupIntegrationComponent implements OnInit {
  readonly steps: { id: SetupStep; label: string; desc: string }[] = [
    { id: 'connect', label: 'Connect', desc: 'Authorize ClickUp' },
    { id: 'configure', label: 'Configure', desc: 'List & fields' },
    { id: 'sync', label: 'Sync', desc: 'Push & pull tasks' },
    { id: 'advanced', label: 'Advanced', desc: 'Mappings & JSON' },
  ];

  activeStep: SetupStep = 'connect';
  projectId: number | null = null;
  token = '';
  authMethod: AuthMethod = 'oauth';
  oauthAvailable = false;
  connectingOAuth = false;
  oauthSetup: any = null;

  clickupUrl = '';
  listId = '';
  workspaceId = '';
  sigmatUrlBase = '';

  customFieldsJson = `{
  "sigmat_project_id": "",
  "sigmat_entity": "",
  "sigmat_entity_id": "",
  "sigmat_url": "",
  "sigmat_hash": "",
  "sigmat_percent_complete": ""
}`;

  fieldDirectionsJson = `{
  "name": "sigmat_to_clickup",
  "description": "sigmat_to_clickup",
  "start_date": "sigmat_to_clickup",
  "due_date": "sigmat_to_clickup",
  "percent_complete": "sigmat_to_clickup",
  "status": "clickup_to_sigmat",
  "assignees": "clickup_to_sigmat"
}`;

  statusMessage = '';
  statusTone: StatusTone = 'info';
  statusData: any = null;
  listPreview: any = null;
  listStatuses: { status: string; color?: string; type?: string }[] = [];
  updatingTaskId: string | null = null;

  loadingStatus = false;
  loadingList = false;
  savingConfig = false;
  pushing = false;
  pushingWorkOrders = false;
  pulling = false;
  pullingAll = false;
  registeringWebhook = false;
  unregisteringWebhook = false;
  webhookEndpoint = '';

  showDebugJson = false;
  pullTaskId = '';

  mapClickupUserId = '';
  mapClickupEmail = '';
  mapSigmatStaffId: number | null = null;

  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.api.clickupOAuthMeta().subscribe({
      next: (res: any) => {
        this.oauthAvailable = !!res?.data?.oauth_available;
        this.oauthSetup = res?.data || null;
        if (!this.oauthAvailable) this.authMethod = 'token';
      },
      error: () => {
        this.oauthAvailable = false;
        this.authMethod = 'token';
      },
    });

    this.route.queryParams.subscribe((params) => {
      if (params['oauth'] === 'success') {
        const count = params['workspaces'];
        this.setStatus(
          count
            ? `ClickUp connected — ${count} workspace(s) authorized`
            : 'ClickUp connected successfully',
          'success'
        );
        this.activeStep = 'configure';
        this.clearOAuthQueryParams();
        this.loadStatus();
      } else if (params['oauth'] === 'error') {
        this.setStatus(`OAuth failed: ${params['message'] || 'unknown error'}`, 'error');
        this.clearOAuthQueryParams();
      }
    });

    this.loadStatus();
  }

  get isConnected(): boolean {
    return !!this.statusData?.connected;
  }

  get connectionLabel(): string {
    if (!this.isConnected) return 'Not connected';
    return this.statusData?.auth_type === 'oauth' ? 'Connected via OAuth' : 'Connected via token';
  }

  get configReady(): boolean {
    return !!this.listId?.trim();
  }

  get canSync(): boolean {
    return this.isConnected && this.configReady && !!this.projectId;
  }

  get webhookRegistered(): boolean {
    return !!this.statusData?.config?.webhook_id;
  }

  get webhookInfo(): { id?: string; endpoint?: string; registered_at?: string } {
    const cfg = this.statusData?.config || {};
    return {
      id: cfg.webhook_id || undefined,
      endpoint: cfg.webhook_endpoint || undefined,
      registered_at: cfg.webhook_registered_at || undefined,
    };
  }

  setStep(step: SetupStep): void {
    this.activeStep = step;
  }

  nextStep(): void {
    const idx = this.steps.findIndex((s) => s.id === this.activeStep);
    if (idx < this.steps.length - 1) {
      this.activeStep = this.steps[idx + 1].id;
    }
  }

  private setStatus(message: string, tone: StatusTone = 'info'): void {
    this.statusMessage = message;
    this.statusTone = tone;
  }

  private clearOAuthQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { oauth: null, message: null, workspaces: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadStatus(): void {
    this.loadingStatus = true;
    this.api.clickupStatus(this.projectId || undefined).subscribe({
      next: (res: any) => {
        this.statusData = res.data;
        this.oauthAvailable = res.data?.oauth_available ?? this.oauthAvailable;
        if (res.data?.connected) {
          const via = res.data.auth_type === 'oauth' ? 'OAuth' : 'personal token';
          this.setStatus(`Connected via ${via}`, 'success');
          if (res.data.auth_type === 'oauth') this.authMethod = 'oauth';
          if (this.activeStep === 'connect' && this.configReady) {
            this.activeStep = 'configure';
          }
        } else if (!this.statusMessage.startsWith('OAuth failed')) {
          this.setStatus('Connect ClickUp to get started', 'info');
        }
        this.applyConfigFromStatus(res.data?.config);
        this.loadingStatus = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Status failed', 'error');
        this.loadingStatus = false;
      },
    });
  }

  private applyConfigFromStatus(config: any): void {
    if (!config) return;
    if (config.list_id) this.listId = String(config.list_id);
    if (config.workspace_id) this.workspaceId = String(config.workspace_id);
    if (config.sigmat_url_base) this.sigmatUrlBase = config.sigmat_url_base;
    if (config.custom_fields && Object.keys(config.custom_fields).length) {
      this.customFieldsJson = JSON.stringify(config.custom_fields, null, 2);
    }
    if (config.field_directions && Object.keys(config.field_directions).length) {
      this.fieldDirectionsJson = JSON.stringify(config.field_directions, null, 2);
    }
  }

  connectWithOAuth(): void {
    if (!this.oauthAvailable) {
      this.setStatus('OAuth not configured on server — use personal token or set env vars', 'warning');
      return;
    }
    this.connectingOAuth = true;
    this.api.clickupOAuthStart(this.projectId || undefined).subscribe({
      next: (res: any) => {
        const url = res?.data?.url;
        if (!url) {
          this.setStatus('OAuth start failed — no redirect URL', 'error');
          this.connectingOAuth = false;
          return;
        }
        window.location.href = url;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'OAuth start failed', 'error');
        this.connectingOAuth = false;
      },
    });
  }

  connectWithToken(): void {
    if (!this.token.trim()) {
      this.setStatus('Enter your ClickUp personal token (pk_...)', 'warning');
      return;
    }
    this.api.clickupConnect({ project_id: this.projectId || undefined, token: this.token.trim() }).subscribe({
      next: () => {
        this.setStatus('Connected via personal token', 'success');
        this.token = '';
        this.activeStep = 'configure';
        this.loadStatus();
      },
      error: (e) => this.setStatus(e?.error?.error || e?.error?.message || 'Connect failed', 'error'),
    });
  }

  disconnect(): void {
    if (!confirm('Disconnect ClickUp for this scope?')) return;
    this.api.clickupDisconnect(this.projectId || undefined).subscribe({
      next: () => {
        this.setStatus('Disconnected', 'info');
        this.statusData = null;
        this.listPreview = null;
        this.activeStep = 'connect';
        this.loadStatus();
      },
      error: (e) => this.setStatus(e?.error?.error || e?.error?.message || 'Disconnect failed', 'error'),
    });
  }

  loadFromUrl(): void {
    const url = this.clickupUrl.trim();
    if (!url) {
      this.setStatus('Paste a ClickUp list URL first', 'warning');
      return;
    }
    if (!this.isConnected) {
      this.setStatus('Connect ClickUp before loading a list', 'warning');
      this.activeStep = 'connect';
      return;
    }
    this.loadingList = true;
    this.listPreview = null;
    this.api.clickupLoadList({ project_id: this.projectId || undefined, url }).subscribe({
      next: (res: any) => {
        const data = res.data;
        this.listPreview = data;
        this.listStatuses = data.statuses || [];
        this.listId = String(data.list_id || '');
        if (data.workspace_id) this.workspaceId = String(data.workspace_id);
        if (data.custom_fields) {
          this.customFieldsJson = JSON.stringify(data.custom_fields, null, 2);
        }
        this.setStatus(
          `Loaded "${data.list?.name || data.list_id}" — ${data.task_count} task(s), ${data.clickup_fields?.length || 0} field(s)`,
          'success'
        );
        this.loadingList = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Load list failed', 'error');
        this.loadingList = false;
      },
    });
  }

  saveConfig(): void {
    let custom_fields: any = {};
    let field_directions: any = {};
    try {
      custom_fields = JSON.parse(this.customFieldsJson || '{}');
    } catch {
      this.setStatus('Invalid custom fields JSON', 'error');
      return;
    }
    try {
      field_directions = JSON.parse(this.fieldDirectionsJson || '{}');
    } catch {
      this.setStatus('Invalid field directions JSON', 'error');
      return;
    }
    if (!this.listId.trim()) {
      this.setStatus('List ID is required', 'warning');
      return;
    }
    const config: any = {
      list_id: this.listId.trim(),
      sigmat_url_base: this.sigmatUrlBase.trim() || undefined,
      custom_fields,
      field_directions,
    };
    if (this.workspaceId.trim()) config.workspace_id = this.workspaceId.trim();

    this.savingConfig = true;
    this.api.clickupUpdateConfig({ project_id: this.projectId || undefined, config }).subscribe({
      next: (res: any) => {
        this.setStatus('Configuration saved', 'success');
        this.statusData = { ...(this.statusData || {}), config: res.data };
        this.savingConfig = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Save config failed', 'error');
        this.savingConfig = false;
      },
    });
  }

  pushProjectWbs(): void {
    if (!this.projectId) {
      this.setStatus('Enter a SigmaT Project ID to sync', 'warning');
      this.activeStep = 'sync';
      return;
    }
    if (!this.configReady) {
      this.setStatus('Save list configuration first', 'warning');
      this.activeStep = 'configure';
      return;
    }
    this.pushing = true;
    this.api.clickupPushProjectWbs(this.projectId).subscribe({
      next: (res: any) => {
        const d = res.data || {};
        this.setStatus(
          `Push complete — created ${d.pushed || 0}, updated ${d.updated || 0}, skipped ${d.skipped || 0}, errors ${d.errors || 0}`,
          (d.errors || 0) > 0 ? 'warning' : 'success'
        );
        this.pushing = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Push failed', 'error');
        this.pushing = false;
      },
    });
  }

  pushWorkOrders(): void {
    if (!this.projectId) {
      this.setStatus('Enter a SigmaT Project ID to sync', 'warning');
      this.activeStep = 'sync';
      return;
    }
    if (!this.configReady) {
      this.setStatus('Save list configuration first', 'warning');
      this.activeStep = 'configure';
      return;
    }
    this.pushingWorkOrders = true;
    this.api.clickupPushProjectWorkOrders(this.projectId).subscribe({
      next: (res: any) => {
        const d = res.data || {};
        this.setStatus(
          `Work orders pushed — created ${d.pushed || 0}, updated ${d.updated || 0}, skipped ${d.skipped || 0}, errors ${d.errors || 0}`,
          (d.errors || 0) > 0 ? 'warning' : 'success'
        );
        this.pushingWorkOrders = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Work order push failed', 'error');
        this.pushingWorkOrders = false;
      },
    });
  }

  pullAllTasks(): void {
    if (!this.projectId) {
      this.setStatus('Enter a SigmaT Project ID to sync', 'warning');
      return;
    }
    this.pullingAll = true;
    this.api.clickupPullProjectTasks(this.projectId).subscribe({
      next: (res: any) => {
        const d = res.data || {};
        this.setStatus(
          `Pull complete — updated ${d.updated || 0}, skipped ${d.skipped || 0}, unmapped ${d.unmapped || 0}, errors ${d.errors || 0} of ${d.total || 0} task(s)`,
          (d.errors || 0) > 0 ? 'warning' : 'success'
        );
        this.pullingAll = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Bulk pull failed', 'error');
        this.pullingAll = false;
      },
    });
  }

  registerWebhook(): void {
    if (!this.configReady) {
      this.setStatus('Save list configuration first', 'warning');
      this.activeStep = 'configure';
      return;
    }
    this.registeringWebhook = true;
    this.api
      .clickupRegisterWebhook({
        project_id: this.projectId || undefined,
        endpoint: this.webhookEndpoint.trim() || undefined,
      })
      .subscribe({
        next: (res: any) => {
          const d = res.data || {};
          this.setStatus(
            `Live sync enabled — webhook ${d.webhook_id} → ${d.endpoint}${d.secret_stored ? ' (signature verification on)' : ''}`,
            'success'
          );
          this.registeringWebhook = false;
          this.loadStatus();
        },
        error: (e) => {
          this.setStatus(e?.error?.error || e?.error?.message || 'Webhook registration failed', 'error');
          this.registeringWebhook = false;
        },
      });
  }

  unregisterWebhook(): void {
    if (!this.webhookRegistered) return;
    if (!confirm('Disable live sync (delete the ClickUp webhook)?')) return;
    this.unregisteringWebhook = true;
    this.api.clickupUnregisterWebhook(this.projectId || undefined).subscribe({
      next: () => {
        this.setStatus('Live sync disabled — webhook removed', 'info');
        this.unregisteringWebhook = false;
        this.loadStatus();
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Webhook removal failed', 'error');
        this.unregisteringWebhook = false;
      },
    });
  }

  pullTask(): void {
    if (!this.projectId) {
      this.setStatus('Enter a SigmaT Project ID to sync', 'warning');
      return;
    }
    if (!this.pullTaskId.trim()) {
      this.setStatus('Enter a ClickUp task ID', 'warning');
      return;
    }
    this.pulling = true;
    this.api.clickupPullTask(this.projectId, this.pullTaskId.trim()).subscribe({
      next: (res: any) => {
        this.setStatus(`Pull: ${res.data?.message || 'Success'}`, 'success');
        this.pulling = false;
      },
      error: (e) => {
        this.setStatus(e?.error?.error || e?.error?.message || 'Pull failed', 'error');
        this.pulling = false;
      },
    });
  }

  saveUserMap(): void {
    if (!this.mapClickupUserId.trim()) {
      this.setStatus('ClickUp user id required', 'warning');
      return;
    }
    this.api
      .clickupUpsertUserMap({
        project_id: this.projectId || undefined,
        clickup_user_id: this.mapClickupUserId.trim(),
        clickup_email: this.mapClickupEmail.trim() || undefined,
        sigmat_staff_id: this.mapSigmatStaffId ?? undefined,
      })
      .subscribe({
        next: () => this.setStatus('Assignee mapping saved', 'success'),
        error: (e) => this.setStatus(e?.error?.error || e?.error?.message || 'User map failed', 'error'),
      });
  }

  copyRedirectUri(): void {
    const uri = this.oauthSetup?.redirect_uri;
    if (!uri) return;
    navigator.clipboard?.writeText(uri);
    this.setStatus('Copied redirect URI — paste into ClickUp app settings', 'info');
  }

  copyTaskId(taskId: string): void {
    navigator.clipboard?.writeText(taskId);
    this.setStatus(`Copied task ID ${taskId}`, 'info');
  }

  updateTaskStatus(task: { id: string; name: string; status?: string }, newStatus: string): void {
    if (!newStatus || newStatus === task.status) return;
    if (!this.isConnected) {
      this.setStatus('Connect ClickUp first', 'warning');
      return;
    }
    this.updatingTaskId = task.id;
    this.api
      .clickupUpdateTaskStatus({
        project_id: this.projectId || undefined,
        task_id: task.id,
        status: newStatus,
      })
      .subscribe({
        next: (res: any) => {
          task.status = res.data?.status || newStatus;
          const hit = this.listPreview?.tasks?.find((t: { id: string }) => t.id === task.id);
          if (hit) {
            hit.status = task.status;
            if (res.data?.status_color) hit.status_color = res.data.status_color;
          }
          this.setStatus(`"${task.name}" → ${task.status}`, 'success');
          this.updatingTaskId = null;
        },
        error: (e) => {
          this.setStatus(e?.error?.error || e?.error?.message || 'Status update failed', 'error');
          this.updatingTaskId = null;
        },
      });
  }
}
